import { mergeSavedLead, mergeNoteUpdate, type NoteUpdateHandler } from "@/lib/note-ui";
import { useState, useEffect, useMemo, useRef } from "react";
import { KanbanBoard } from "@/components/kanban-board";
import { Lead, LeadStatus, HistoryEntry, LeadCard } from "@/components/lead-card";
import { LeadDrawer } from "@/components/lead-drawer";
import { CSVImport } from "@/components/csv-import";
import { useToast } from "@/hooks/use-toast";
import { AppSidebar } from "@/components/app-sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Filter,
  Search,
  FileUp,
  List,
  LayoutGrid,
  Table as TableIcon,
  Activity,
  ChevronLeft,
  Plus,
  Users,
  SlidersHorizontal,
  MoreHorizontal,
  ArrowUpDown,
  FileText,
  Download,
  Archive,
  Calendar,
  FolderOpen,
  FolderPlus,
  Pencil,
  Trash2,
  Check,
  X,
  RefreshCw
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from "@/components/ui/select";
import { useFolders } from "@/hooks/use-folders";
import { useHideDeactivatedUsers } from "@/hooks/use-hide-deactivated-users";
import { Folder } from "@/services/foldersService";
import {
  fetchLeads,
  fetchLeadsForCurrentUser,
  fetchLeadsForCurrentUserPaginated,
  PaginatedLeadsResult,
  createLead,
  updateLead,
  deleteLead,
  fetchUserLeads,
} from "@/services/leadsService";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { addOldContactToMarketplace } from "@/services/marketLeadsService";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LeadTimeline } from "@/components/lead-timeline";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { logger } from "@/lib/logger";
import { useVirtualizer } from "@tanstack/react-virtual";

type ViewType = "board" | "list" | "table" | "timeline";
type SortField = "status" | "name" | "position" | "priority" | "nextContactDate" | "lastContactDate" | "attachment";
type SortDirection = "asc" | "desc";

export default function UnifiedLeadsPage() {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedLead, setSelectedLead] = useState<Lead | undefined>(undefined);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isNewLead, setIsNewLead] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState<ViewType>("board");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [assignedToFilter, setAssignedToFilter] = useState<string>("all");
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isMoveToOldContactsDialogOpen, setIsMoveToOldContactsDialogOpen] = useState(false);
  const [leadsToMove, setLeadsToMove] = useState<Lead[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isStatusSelectionDialogOpen, setIsStatusSelectionDialogOpen] = useState(false);
  const [hideOldContacts, setHideOldContacts] = useState(false);
  const [hideDeactivatedUsers, setHideDeactivatedUsers] = useHideDeactivatedUsers();
  const [timeRange, setTimeRange] = useState<"all" | "today" | "week" | "month">("all");

  // Folder state
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isFolderManagementOpen, setIsFolderManagementOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState("#3B82F6");
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);

  // Pagination state for better performance
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const PAGE_SIZE = 100; // Leads per page
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Predefined folder colors
  const FOLDER_COLORS = [
    "#3B82F6", // Blue
    "#10B981", // Green
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#06B6D4", // Cyan
    "#F97316", // Orange
    "#6366F1", // Indigo
    "#84CC16", // Lime
  ];

  // Ref for virtual scrolling in list view
  const listParentRef = useRef<HTMLDivElement>(null);

  // Get folders for the selected user (or current user)
  const {
    folders,
    isLoading: foldersLoading,
    createFolder: handleCreateFolder,
    renameFolder: handleRenameFolder,
    deleteFolder: handleDeleteFolder,
    updateFolderColor: handleUpdateFolderColor,
    refetch: refetchFolders
  } = useFolders(selectedUserId || currentUser?.id);

  // Manual refresh handler
  const handleManualRefresh = async () => {
    if (!currentUser) return;
    setIsRefreshing(true);
    try {
      const freshLeads = await fetchLeadsForCurrentUser(
        selectedUserId || currentUser.id,
        selectedUserId ? 'user' : currentUser.role,
        currentUser.tenantId,
        true // Skip cache
      );
      setLeads(freshLeads);
      setTotalLeadsCount(freshLeads.length);
      toast({
        title: "Daten aktualisiert",
        description: "Leads wurden neu geladen"
      });
    } catch (error) {
      logger.error("Error refreshing leads:", error);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht aktualisiert werden",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load leads from Firestore with PAGINATION for better performance
  useEffect(() => {
    const loadLeads = async () => {
      setIsLoading(true);
      // Reset pagination state on initial load
      setLastDoc(null);
      setHasMore(true);

      try {
        // First, get all users from current tenant
        const usersRef = collection(db, "users");
        let usersQuery;

        // For legacy tenant, fetch all users and filter client-side
        // For new tenants, filter by tenantId
        if (currentUser.tenantId === "legacy") {
          usersQuery = usersRef;
        } else {
          usersQuery = query(usersRef, where("tenantId", "==", currentUser.tenantId));
        }

        const usersSnapshot = await getDocs(usersQuery);
        const usersData = new Map();

        usersSnapshot.forEach(doc => {
          const userData = doc.data() as any;

          // For legacy tenant, exclude users from other tenants
          if (currentUser.tenantId === "legacy") {
            if (userData.tenantId && userData.tenantId !== "legacy") {
              return; // Skip users from other tenants
            }
          }

          usersData.set(doc.id, {
            id: doc.id,
            name: userData.name || "Unbekannt",
            email: userData.email || "",
            role: userData.role || "",
            active: userData.active !== undefined ? userData.active : true,
          });
        });

        // Determine if we should use pagination or load all
        // Pagination ONLY for admins viewing "Alle Leads"
        // For specific users or regular users: load ALL their leads immediately
        const isAdminViewingAll = (currentUser.role === 'admin' || currentUser.role === 'developer')
          && selectedUserId === 'all';

        let firestoreLeads: Lead[] = [];

        if (isAdminViewingAll) {
          // Admin viewing "Alle Leads" - use pagination
          const result = await fetchLeadsForCurrentUserPaginated(
            currentUser.id,
            currentUser.role,
            currentUser.tenantId,
            PAGE_SIZE,
            null
          );
          firestoreLeads = result.leads;
          setLastDoc(result.lastDoc);
          setHasMore(result.hasMore);
        } else {
          // Specific user selected OR regular user - load ALL leads immediately (no pagination)
          firestoreLeads = await fetchLeadsForCurrentUser(
            selectedUserId || currentUser.id,
            // Treat as regular user to get only that user's leads
            selectedUserId ? 'user' : currentUser.role,
            currentUser.tenantId,
            false // Use cache
          );
          setLastDoc(null);
          setHasMore(false); // No "load more" needed
        }

        setTotalLeadsCount(firestoreLeads.length);

        // For admin/developer, set up user grouping
        if (currentUser?.role === "admin" || currentUser?.role === "developer") {
          // Group leads by user for the admin view
          const usersMap = new Map();

          // Initialize users map with all users from Firestore
          // Admins don't see developers, but developers see everyone
          usersData.forEach((userData, userId) => {
            // Hide developers from admin users
            if (currentUser?.role === "admin" && userData.role === "developer") {
              return; // Skip developers for admin users
            }
            usersMap.set(userId, {
              ...userData,
              leads: []
            });
          });

          // Then process leads and assign user data
          firestoreLeads.forEach(lead => {
            if (lead.assignedTo) {
              if (!usersMap.has(lead.assignedTo)) {
                // If user not found in usersData, create a placeholder
                usersMap.set(lead.assignedTo, {
                  id: lead.assignedTo,
                  name: "Unbekannt",
                  email: "",
                  role: "",
                  leads: []
                });
              }
              usersMap.get(lead.assignedTo).leads.push(lead);
            }
          });

          setUsers(Array.from(usersMap.values()));

          // For admin, default to showing their own leads
          if (!selectedUserId) {
            setSelectedUserId(currentUser.id);
          }
        } else {
          // For berater/regular users, always show their own leads
          setSelectedUserId(currentUser.id);
        }

        setLeads(firestoreLeads);
      } catch (error) {
        console.error("Error loading leads:", error);
        toast({
          title: "Fehler beim Laden der Leads",
          description: "Die Leads konnten nicht geladen werden.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadLeads();
    }
  }, [toast, currentUser, selectedUserId]);

  // Load more leads (pagination) - ONLY for admins viewing "Alle Leads"
  const loadMoreLeads = async () => {
    // Only allow loading more when admin is viewing "Alle Leads"
    const isAdminViewingAll = (currentUser?.role === 'admin' || currentUser?.role === 'developer')
      && selectedUserId === 'all';

    if (!hasMore || isLoadingMore || !currentUser || !isAdminViewingAll) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchLeadsForCurrentUserPaginated(
        currentUser.id,
        currentUser.role,
        currentUser.tenantId,
        PAGE_SIZE,
        lastDoc
      );

      // Append new leads to existing ones
      setLeads(prev => [...prev, ...result.leads]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
      setTotalLeadsCount(prev => prev + result.leads.length);

      // Update users map with new leads for admin view
      setUsers(prevUsers => {
        const usersMap = new Map(prevUsers.map(u => [u.id, { ...u }]));

        result.leads.forEach(lead => {
          if (lead.assignedTo && usersMap.has(lead.assignedTo)) {
            usersMap.get(lead.assignedTo).leads.push(lead);
          }
        });

        return Array.from(usersMap.values());
      });
    } catch (error) {
      console.error("Error loading more leads:", error);
      toast({
        title: "Fehler",
        description: "Weitere Leads konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Listen for lead updates from chatbot
  useEffect(() => {
    const handleLeadUpdate = (event: CustomEvent) => {
      logger.info('Lead update received from chatbot:', event.detail);
      // Force reload leads to ensure UI is up to date
      if (currentUser) {
        const loadLeads = async () => {
          try {
            let firestoreLeads: Lead[] = [];

            // Use the new role-aware function
            firestoreLeads = await fetchLeadsForCurrentUser(
              currentUser.id,
              currentUser.role,
              currentUser.tenantId,
              true // Skip cache for real-time updates
            );

            setLeads(firestoreLeads);
          } catch (error) {
            logger.error("Error reloading leads after chatbot update:", error);
          }
        };
        loadLeads();
      }
    };

    window.addEventListener('leadUpdated', handleLeadUpdate as EventListener);

    return () => {
      window.removeEventListener('leadUpdated', handleLeadUpdate as EventListener);
    };
  }, [currentUser]);

  // Handle lead status change
  const handleLeadStatusChange = async (leadId: string, newStatus: LeadStatus) => {
    try {
      // Find the original lead to track changes
      const originalLead = leads.find(lead => lead.id === leadId);

      if (!originalLead) {
        console.error("Lead not found for status change");
        return;
      }

      // Create changes array (same pattern as lead form)
      const changes = [{
        field: "status",
        oldValue: originalLead.status,
        newValue: newStatus
      }];

      // Create updated history (same pattern as lead form)
      const updatedHistory = [...(originalLead.history || [])];
      updatedHistory.push({
        date: new Date().toISOString(),
        type: "updated" as const,
        changes,
        user: currentUser ? currentUser.id : "System"
      });

      // Create update data with history (same pattern as lead form)
      const updateData = {
        status: newStatus,
        history: updatedHistory
      };

      const success = await updateLead(leadId, updateData, originalLead);

      if (success) {
        // Update the lead in the local state with the new status and history
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? mergeSavedLead(lead, updateData, originalLead) : lead
          )
        );
      }
    } catch (error) {
      console.error("Error updating lead status:", error);
      toast({
        title: "Fehler",
        description: "Der Status konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  // Handle lead click - ensure we get the latest data including history
  const handleLeadClick = (lead: Lead) => {
    // Find the most up-to-date version of the lead from the state
    const freshLead = leads.find(l => l.id === lead.id);
    console.log("Clicked lead:", lead);
    console.log("Fresh lead from state:", freshLead);

    if (freshLead) {
      setSelectedLead(freshLead);
    } else {
      setSelectedLead(lead);
    }

    setIsNewLead(false);
    setIsFormOpen(true);
  };

  // Handle creating a new lead
  const handleNewLead = () => {
    setSelectedLead(undefined);
    setIsNewLead(true);
    setIsFormOpen(true);
  };

  // Handle creating a new lead with specific status
  const handleNewLeadInColumn = (status: LeadStatus) => {
    setSelectedLead({
      ...undefined as any, // Clear any previous lead
      status: status // Set the initial status based on the column
    });
    setIsNewLead(true);
    setIsFormOpen(true);
  };

  // Handle form submission
  const handleLeadSubmit = async (leadData: Partial<Lead>) => {
    try {
      console.log("Lead form submitted with data:", leadData);

      // Ensure all fields have valid values to prevent Firestore errors
      const sanitizedData: Partial<Lead> = {};

      // Only include defined fields
      Object.entries(leadData).forEach(([key, value]) => {
        if (value !== undefined) {
          // Use type assertion with an object index signature
          (sanitizedData as { [key: string]: any })[key] = value;
        }
      });

      // Ensure empty string for position is preserved
      if (leadData.position !== undefined) {
        sanitizedData.position = leadData.position;
      }

      if (isNewLead) {
        // Create new lead in Firestore
        const newLeadData = {
          firstName: sanitizedData.firstName || "",
          lastName: sanitizedData.lastName || "",
          position: sanitizedData.position || "",
          email: sanitizedData.email || "",
          phone: sanitizedData.phone || "",
          status: sanitizedData.status || "new",
          priority: sanitizedData.priority || "medium",
          nextContactDate: sanitizedData.nextContactDate || "",
          lastContactDate: sanitizedData.lastContactDate || "",
          notes: sanitizedData.notes || "",
          notesHistory: sanitizedData.notesHistory || [],
          assignedTo: currentUser ? currentUser.id : "",
          attachment: sanitizedData.attachment || "",
          dataProtectionConsent: sanitizedData.dataProtectionConsent || false,
        };

        const newLead = await createLead(newLeadData);

        if (newLead) {
          setLeads(prevLeads => [newLead, ...prevLeads]);
          toast({
            title: "Lead erstellt",
            description: `${newLead.firstName} ${newLead.lastName} wurde erfolgreich erstellt`,
          });
        }
      } else if (selectedLead) {
        // Update existing lead in Firestore
        // Create a new history entry
        const changes: { field: string, oldValue: any, newValue: any }[] = [];

        // Compare fields to find changes (exclude notesHistory from change tracking)
        Object.keys(sanitizedData).forEach(key => {
          const typedKey = key as keyof Lead;
          // Skip notesHistory from change tracking as it has its own management
          if (key === 'notesHistory') return;

          if (sanitizedData[typedKey] !== undefined &&
            JSON.stringify(sanitizedData[typedKey]) !== JSON.stringify(selectedLead[typedKey])) {
            changes.push({
              field: key,
              oldValue: selectedLead[typedKey],
              newValue: sanitizedData[typedKey]
            });
          }
        });

        // Prepare updated lead with new history
        const updatedHistory = [...(selectedLead.history || [])];

        if (changes.length > 0) {
          updatedHistory.push({
            date: new Date().toISOString(),
            type: "updated" as const,
            changes,
            user: currentUser ? currentUser.id : "System"
          });
        }

        // Notes are saved separately; do not write back a stale selected-lead snapshot.
        const updateData = {
          ...sanitizedData,
          history: updatedHistory
        };

        logger.log("Updating lead with data:", updateData);
        const success = await updateLead(selectedLead.id, updateData, selectedLead);

        if (success) {
          // Update local state with the updated lead including history
          setLeads(prevLeads =>
            prevLeads.map(lead => {
              if (lead.id === selectedLead.id) {
                return mergeSavedLead(lead, updateData, selectedLead);
              }
              return lead;
            })
          );

          // Update the selectedLead to ensure it reflects the latest data
          setSelectedLead(prev => {
            if (!prev) return prev;
            return prev.id === selectedLead.id ? mergeSavedLead(prev, updateData, selectedLead) : prev;
          });

          toast({
            title: "Lead aktualisiert",
            description: `${sanitizedData.firstName} ${sanitizedData.lastName} wurde erfolgreich aktualisiert`,
          });
        }
      }
    } catch (error) {
      logger.error("Error submitting form:", error);
      toast({
        title: "Fehler",
        description: "Beim Speichern des Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsFormOpen(false);
    }
  };

  // Handle CSV import
  const handleImportLeads = async (importedLeads: Partial<Lead>[], folderId?: string) => {
    const newLeads: Lead[] = [];

    try {
      for (const leadData of importedLeads) {
        const newLeadData = {
          firstName: leadData.firstName || "",
          lastName: leadData.lastName || "",
          email: leadData.email || "",
          position: leadData.position || "",
          phone: leadData.phone || "",
          status: leadData.status || "new",
          priority: leadData.priority || "medium",
          nextContactDate: leadData.nextContactDate,
          lastContactDate: leadData.lastContactDate,
          notes: leadData.notes || "",
          assignedTo: currentUser ? currentUser.id : "",
          attachment: leadData.attachment || "",
          ...(folderId && { folderId }),
          history: [{
            date: new Date().toISOString(),
            type: "created" as const,
            changes: [],
            user: currentUser ? currentUser.id : "System"
          }],
        };

        const newLead = await createLead(newLeadData);
        if (newLead) {
          newLeads.push(newLead);
        }
      }

      if (newLeads.length > 0) {
        setLeads(prev => [...newLeads, ...prev]);
        toast({
          title: "Leads importiert",
          description: `${newLeads.length} Leads wurden erfolgreich importiert`,
        });
      }
    } catch (error) {
      console.error("Error importing leads:", error);
      toast({
        title: "Fehler beim Import",
        description: "Beim Import der Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsImportOpen(false);
    }
  };

  // Sort leads based on current sort field and direction
  const sortLeads = (leadsToSort: Lead[]): Lead[] => {
    return [...leadsToSort].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases for date fields
      if (sortField === "lastContactDate" || sortField === "nextContactDate") {
        // Convert empty strings to a far future date for sorting
        const aDate = aValue ? new Date(aValue as string).getTime() : Number.MAX_SAFE_INTEGER;
        const bDate = bValue ? new Date(bValue as string).getTime() : Number.MAX_SAFE_INTEGER;

        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // Handle empty strings for attachment field
        if (sortField === "attachment") {
          aValue = aValue || "";
          bValue = bValue || "";
        }
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Default comparison for other types
      if (aValue === bValue) return 0;

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });
  };

  // Toggle sort direction or change sort field
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field with default ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filter leads based on search query, status, priority and assignment
  // Memoized for performance - prevents recalculation on every render
  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        searchQuery === "" ||
        (lead.firstName?.toLowerCase() || "").includes(searchLower) ||
        (lead.lastName?.toLowerCase() || "").includes(searchLower) ||
        (lead.position?.toLowerCase() || "").includes(searchLower) ||
        (lead.email?.toLowerCase() || "").includes(searchLower) ||
        (lead.phone?.toLowerCase() || "").includes(searchLower);

      const matchesStatus =
        statusFilter === "all" ||
        lead.status === statusFilter;

      const matchesPriority =
        priorityFilter === "all" ||
        lead.priority === priorityFilter;

      // Filter based on selected user
      const matchesUser =
        // For admin and developer users
        ((currentUser?.role === "admin" || currentUser?.role === "developer") && selectedUserId === "all") || // Show all leads when "Alle Leads" is selected
        ((currentUser?.role === "admin" || currentUser?.role === "developer") && selectedUserId === lead.assignedTo) || // Show selected user's leads
        // For co-admin and regular users, only show their own leads
        ((currentUser?.role !== "admin" && currentUser?.role !== "developer") && lead.assignedTo === currentUser?.id);

      // Filter out leads that have been marked as old contacts through the system
      const isSystemOldContact = lead.marketplacePurchaseInfo?.includes("Über Marktplatz zugewiesen von System") || false;
      const matchesOldContactsFilter = !hideOldContacts || !isSystemOldContact;

      // Time Range Filter
      let matchesTimeRange = true;
      if (timeRange !== "all") {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Check both nextContactDate and lastContactDate
        const nextContact = lead.nextContactDate ? new Date(lead.nextContactDate) : null;
        const lastContact = lead.lastContactDate ? new Date(lead.lastContactDate) : null;

        const checkDate = (date: Date | null) => {
          if (!date) return false;
          const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

          if (timeRange === "today") {
            return d.getTime() === today.getTime();
          } else if (timeRange === "week") {
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay()); // Sunday
            const weekEnd = new Date(today);
            weekEnd.setDate(today.getDate() + (6 - today.getDay())); // Saturday
            return d >= weekStart && d <= weekEnd;
          } else if (timeRange === "month") {
            return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
          }
          return false;
        };

        matchesTimeRange = checkDate(nextContact) || checkDate(lastContact);
      }

      // Folder filter - client-side filtering for now
      const matchesFolder =
        selectedFolderId === null || // "All Leads" - show all
        (selectedFolderId === "__NO_FOLDER__" && !lead.folderId) || // "No Folder" - only unassigned
        lead.folderId === selectedFolderId; // Specific folder

      return matchesSearch && matchesStatus && matchesPriority && matchesUser && matchesOldContactsFilter && matchesTimeRange && matchesFolder;
    });
  }, [leads, searchQuery, statusFilter, priorityFilter, selectedUserId, currentUser, hideOldContacts, timeRange, selectedFolderId]);

  // Apply sorting to filtered leads - memoized
  const sortedFilteredLeads = useMemo(() => {
    return sortLeads(filteredLeads);
  }, [filteredLeads, sortField, sortDirection]);

  // Virtual scrolling for list view - renders only visible items for better performance
  // Must be defined after sortedFilteredLeads
  const listVirtualizer = useVirtualizer({
    count: sortedFilteredLeads.length,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => 88, // Estimated height per lead card in list view
    overscan: 5, // Render 5 extra items above/below viewport
  });

  // Handle user selection (for admin view)
  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
  };

  // Handle lead deletion
  const handleLeadDelete = async (leadId: string) => {
    try {
      const success = await deleteLead(leadId);

      if (success) {
        // Update the local state
        setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));

        // Also update the users state for admin view
        setUsers(prevUsers =>
          prevUsers.map(user => ({
            ...user,
            leads: user.leads.filter((lead: Lead) => lead.id !== leadId)
          }))
        );

        toast({
          title: "Lead gelöscht",
          description: "Der Lead wurde erfolgreich gelöscht",
        });
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast({
        title: "Fehler",
        description: "Der Lead konnte nicht gelöscht werden",
        variant: "destructive",
      });
    }
  };

  // Handle lead assignment
  const handleLeadAssign = async (leadId: string, userId: string) => {
    try {
      // Find the original lead to track changes
      const originalLead = leads.find(lead => lead.id === leadId);

      if (!originalLead) {
        console.error("Lead not found for assignment");
        return;
      }

      // Get user names for history
      const fromUserName = users.find(u => u.id === originalLead.assignedTo)?.name || originalLead.assignedTo;
      const toUserName = users.find(u => u.id === userId)?.name || userId;

      // Create the update data with the new assignedTo value
      const updateData: Partial<Lead> = {
        assignedTo: userId,
        // Add a history entry for the assignment with user names
        history: [
          ...(originalLead.history || []),
          {
            date: new Date().toISOString(),
            type: "updated" as const,
            changes: [
              {
                field: "assignedTo",
                oldValue: fromUserName,
                newValue: toUserName
              }
            ],
            user: currentUser?.id || "system"
          }
        ]
      };

      // Preserve marketplace information only if it exists
      if (originalLead.source !== undefined && originalLead.source !== null) {
        updateData.source = originalLead.source;
      }
      if (originalLead.marketLeadId !== undefined && originalLead.marketLeadId !== null) {
        updateData.marketLeadId = originalLead.marketLeadId;
      }
      if (originalLead.marketplacePurchaseInfo !== undefined && originalLead.marketplacePurchaseInfo !== null) {
        updateData.marketplacePurchaseInfo = originalLead.marketplacePurchaseInfo;
      }

      const success = await updateLead(leadId, updateData, originalLead);

      if (success) {
        // Update the lead in the local state
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? mergeSavedLead(lead, updateData, originalLead) : lead
          )
        );

        toast({
          title: "Lead zugewiesen",
          description: "Der Lead wurde erfolgreich einem anderen Benutzer zugewiesen",
        });
      }
    } catch (error) {
      console.error("Error assigning lead:", error);
      toast({
        title: "Fehler",
        description: "Der Lead konnte nicht zugewiesen werden",
        variant: "destructive",
      });
    }
  };

  // Handle lead takeover
  const handleLeadTakeover = async (leadId: string) => {
    if (!currentUser) return;

    try {
      // Find the original lead to track changes
      const originalLead = leads.find(lead => lead.id === leadId);

      if (!originalLead) {
        console.error("Lead not found for takeover");
        return;
      }

      // Get user names for history
      const fromUserName = users.find(u => u.id === originalLead.assignedTo)?.name || originalLead.assignedTo;
      const toUserName = users.find(u => u.id === currentUser.id)?.name || currentUser.id;

      // Create the update data with the current user as assignedTo
      const updateData: Partial<Lead> = {
        assignedTo: currentUser.id,
        // Add a history entry for the takeover with user names
        history: [
          ...(originalLead.history || []),
          {
            date: new Date().toISOString(),
            type: "updated" as const,
            changes: [
              {
                field: "assignedTo",
                oldValue: fromUserName,
                newValue: toUserName
              }
            ],
            user: currentUser.id
          }
        ]
      };

      // Preserve marketplace information only if it exists
      if (originalLead.source !== undefined && originalLead.source !== null) {
        updateData.source = originalLead.source;
      }
      if (originalLead.marketLeadId !== undefined && originalLead.marketLeadId !== null) {
        updateData.marketLeadId = originalLead.marketLeadId;
      }
      if (originalLead.marketplacePurchaseInfo !== undefined && originalLead.marketplacePurchaseInfo !== null) {
        updateData.marketplacePurchaseInfo = originalLead.marketplacePurchaseInfo;
      }

      const success = await updateLead(leadId, updateData, originalLead);

      if (success) {
        // Update the lead in the local state
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? mergeSavedLead(lead, updateData, originalLead) : lead
          )
        );

        toast({
          title: "Lead übernommen",
          description: "Der Lead wurde Ihnen erfolgreich zugewiesen",
        });
      }
    } catch (error) {
      console.error("Error taking over lead:", error);
      toast({
        title: "Fehler",
        description: "Der Lead konnte nicht übernommen werden",
        variant: "destructive",
      });
    }
  };

  // Modify the handleMoveToOldContacts function
  const handleMoveToOldContacts = async () => {
    try {
      // Open the status selection dialog first
      setIsStatusSelectionDialogOpen(true);
    } catch (error) {
      console.error("Error preparing leads for move:", error);
      toast({
        title: "Fehler",
        description: "Beim Vorbereiten der Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    }
  };

  // Handle lead updates from the form (e.g., notes changes)
  const handleLeadUpdate: NoteUpdateHandler = (leadId, mutation, history) => {
    const merge = (lead: Lead) => lead.id === leadId ? mergeNoteUpdate(lead, mutation, history) : lead;
    setLeads(current => current.map(merge));
    setSelectedLead(current => current ? merge(current) : current);
  };

  // Add new function to handle the actual move after status selection
  const handleConfirmStatusSelection = async () => {
    try {
      if (selectedStatuses.length === 0) {
        toast({
          title: "Keine Status ausgewählt",
          description: "Bitte wählen Sie mindestens einen Status aus.",
          variant: "destructive",
        });
        return;
      }

      // Get all leads that match the selected statuses
      const leadsToUpdate = filteredLeads.filter(lead => {
        // Check if lead matches any selected normal status
        if (selectedStatuses.includes(lead.status)) {
          return true;
        }

        // Check if "marketplace-system" is selected and lead is a system marketplace lead
        if (selectedStatuses.includes("marketplace-system")) {
          const isSystemMarketplaceLead = lead.marketplacePurchaseInfo?.includes("Über Marktplatz zugewiesen von System") || false;
          if (isSystemMarketplaceLead) {
            return true;
          }
        }

        return false;
      });

      if (leadsToUpdate.length === 0) {
        toast({
          title: "Keine Leads zum Verschieben",
          description: "Es wurden keine Leads mit den ausgewählten Status gefunden.",
        });
        return;
      }

      // Set the leads to move and open the confirmation dialog
      setLeadsToMove(leadsToUpdate);
      setIsMoveToOldContactsDialogOpen(true);
      setIsStatusSelectionDialogOpen(false);
    } catch (error) {
      console.error("Error preparing leads for move:", error);
      toast({
        title: "Fehler",
        description: "Beim Vorbereiten der Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    }
  };

  // Modify the handleConfirmMoveToOldContacts function
  const handleConfirmMoveToOldContacts = async () => {
    try {
      if (!currentUser?.id) {
        toast({
          title: "Nicht eingeloggt",
          description: "Sie müssen eingeloggt sein, um Leads zu verschieben.",
          variant: "destructive",
        });
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const movedLeadIds: string[] = [];

      console.log("Starting to move leads to marketplace:", leadsToMove);

      // Process each lead
      for (const lead of leadsToMove) {
        try {
          console.log("Processing lead:", lead.id, lead.firstName, lead.lastName);

          // Add the lead to the marketplace as an old contact, always using "System" as the folder
          const listingId = await addOldContactToMarketplace(
            lead,
            currentUser.id,
            currentUser.name || "Unbekannt",
            0, // Default price of 0
            "Altkontakt aus Lead-Liste",
            "System", // Always use "System" as the folder name
            currentUser.tenantId
          );

          console.log("Added to marketplace, listingId:", listingId);

          if (listingId) {
            // Delete the lead from the lead list instead of updating its status
            const success = await deleteLead(lead.id);
            if (success) {
              console.log("Successfully removed lead from lead list");
              successCount++;
              movedLeadIds.push(lead.id);
            } else {
              console.error("Failed to remove lead from lead list");
              errorCount++;
            }
          } else {
            console.error("Failed to add lead to marketplace");
            errorCount++;
          }
        } catch (error) {
          console.error("Error moving lead to marketplace:", error);
          errorCount++;
        }
      }

      // Update local state by removing only the successfully moved leads
      setLeads(prevLeads =>
        prevLeads.filter(lead => !movedLeadIds.includes(lead.id))
      );

      // Show result toast
      if (errorCount === 0) {
        toast({
          title: "Leads verschoben",
          description: `${successCount} Leads wurden erfolgreich in die Leadbörse verschoben.`,
        });
      } else {
        toast({
          title: "Teilweise erfolgreich",
          description: `${successCount} Leads wurden verschoben, ${errorCount} Fehler aufgetreten.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error moving leads to marketplace:", error);
      toast({
        title: "Fehler",
        description: "Beim Verschieben der Leads ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    } finally {
      // Close the dialog and clear the leads to move
      setIsMoveToOldContactsDialogOpen(false);
      setLeadsToMove([]);
      setSelectedStatuses([]);
    }
  };

  const renderTableView = () => {
    // For large datasets, show a performance hint
    const showPerformanceHint = sortedFilteredLeads.length > 500;

    return (
      <div className="bg-white rounded-md shadow">
        {showPerformanceHint && (
          <div className="px-4 py-2 bg-amber-50 text-amber-700 text-sm border-b">
            {sortedFilteredLeads.length} Leads geladen. Für bessere Performance nutzen Sie die Listenansicht oder Filter.
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => handleSort("status")}>
                Status {sortField === "status" && <ArrowUpDown className="inline h-4 w-4 ml-1" />}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                Vorname {sortField === "name" && <ArrowUpDown className="inline h-4 w-4 ml-1" />}
              </TableHead>
              <TableHead>Nachname</TableHead>
              <TableHead>Beruf</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Aufhänger</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("priority")}>
                Priorität {sortField === "priority" && <ArrowUpDown className="inline h-4 w-4 ml-1" />}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("nextContactDate")}>
                Nächster Kontakt {sortField === "nextContactDate" && <ArrowUpDown className="inline h-4 w-4 ml-1" />}
              </TableHead>
              <TableHead>Zugewiesen</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedFilteredLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onClick={handleLeadClick}
                view="table"
                onLeadDelete={handleLeadDelete}
                onLeadAssign={handleLeadAssign}
                onLeadTakeover={handleLeadTakeover}
                users={users}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div ref={listParentRef} className="h-full overflow-auto">
        <div
          style={{
            height: listVirtualizer.getTotalSize(),
            position: 'relative'
          }}
        >
          {listVirtualizer.getVirtualItems().map((virtualRow) => {
            const lead = sortedFilteredLeads[virtualRow.index];
            return (
              <div
                key={lead.id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <LeadCard
                  lead={lead}
                  onClick={handleLeadClick}
                  view="list"
                  onLeadDelete={handleLeadDelete}
                  onLeadAssign={handleLeadAssign}
                  onLeadTakeover={handleLeadTakeover}
                  users={users}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBoardView = () => {
    return (
      <KanbanBoard
        leads={sortedFilteredLeads}
        onLeadClick={handleLeadClick}
        onLeadStatusChange={handleLeadStatusChange}
        onNewLeadInColumn={handleNewLeadInColumn}
        onLeadDelete={handleLeadDelete}
        onLeadAssign={handleLeadAssign}
        onLeadTakeover={handleLeadTakeover}
        users={users}
      />
    );
  };

  const renderTimelineView = () => {
    return (
      <LeadTimeline
        leads={sortedFilteredLeads}
        onLeadClick={handleLeadClick}
      />
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <p>Leads werden geladen...</p>
        </div>
      );
    }

    if (filteredLeads.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="bg-muted/30 rounded-full p-3">
            <Search className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-medium">Keine Leads gefunden</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-sm">
            Es wurden keine Leads mit den angegebenen Filterkriterien gefunden. Versuchen Sie, Ihre Suche anzupassen oder fügen Sie neue Leads hinzu.
          </p>
        </div>
      );
    }

    switch (activeView) {
      case "board":
        return renderBoardView();
      case "list":
        return renderListView();
      case "table":
        return renderTableView();
      case "timeline":
        return renderTimelineView();
      default:
        return renderBoardView();
    }
  };

  // Helper function to get status label in German
  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      "new": "Neu",
      "old-contact": "Altkontakte",
      "not-reached": "Nicht erreicht",
      "follow-up": "Wiedervorlage",
      "scheduled": "Terminiert",
      "in-progress": "In Prozess",
      "won": "Gewonnen",
      "lost": "Verloren",
      "wrong-data": "Falsche Daten",
      "delete-data": "Daten löschen"
    };
    return statusMap[status] || status;
  };

  // Helper function to get priority label in German
  const getPriorityLabel = (priority: string): string => {
    const priorityMap: Record<string, string> = {
      "low": "Niedrig",
      "medium": "Mittel",
      "high": "Hoch"
    };
    return priorityMap[priority] || priority;
  };

  // Export leads to Excel
  const exportToExcel = () => {
    try {
      // Create CSV header with all relevant fields
      let csvContent = "Vorname,Nachname,Position,E-Mail,Telefon,Status,Priorität,Nächster Kontakt,Letzter Kontakt,Notizen,Zugewiesen an,Anhang\n";

      filteredLeads.forEach(lead => {
        // Escape quotes in string fields and wrap in quotes
        const escapeField = (field: any) => {
          if (field === null || field === undefined) return '';
          return `"${String(field).replace(/"/g, '""')}"`;
        };

        // Format dates
        const formatDate = (date: Date | string | undefined) => {
          if (!date) return '';
          try {
            const dateObj = date instanceof Date ? date : new Date(date);
            return format(dateObj, "dd.MM.yyyy HH:mm");
          } catch {
            return String(date);
          }
        };

        csvContent += [
          escapeField(lead.firstName),
          escapeField(lead.lastName),
          escapeField(lead.position),
          escapeField(lead.email),
          escapeField(lead.phone),
          escapeField(getStatusLabel(lead.status)),
          escapeField(getPriorityLabel(lead.priority)),
          escapeField(formatDate(lead.nextContactDate)),
          escapeField(formatDate(lead.lastContactDate)),
          escapeField(lead.notes),
          escapeField(users.find(u => u.id === lead.assignedTo)?.name || lead.assignedTo),
          escapeField(lead.attachment)
        ].join(',') + '\n';
      });

      // Create a blob and generate download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', `leads_export_${format(new Date(), "yyyy-MM-dd")}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export erfolgreich",
        description: `${filteredLeads.length} Leads wurden als Excel-CSV exportiert`,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Export fehlgeschlagen",
        description: "Die Leads konnten nicht exportiert werden.",
        variant: "destructive",
      });
    }
  };

  // Export leads to JSON
  const exportToJSON = () => {
    try {
      // Create a simplified version of leads for export
      const exportLeads = filteredLeads.map(lead => {
        // Format dates
        const formatDate = (date: Date | string | undefined) => {
          if (!date) return '';
          try {
            const dateObj = date instanceof Date ? date : new Date(date);
            return format(dateObj, "dd.MM.yyyy HH:mm");
          } catch {
            return String(date);
          }
        };

        return {
          firstName: lead.firstName,
          lastName: lead.lastName,

          position: lead.position,
          email: lead.email,
          phone: lead.phone,
          status: getStatusLabel(lead.status),
          priority: getPriorityLabel(lead.priority),
          nextContactDate: formatDate(lead.nextContactDate),
          lastContactDate: formatDate(lead.lastContactDate),
          notes: lead.notes,
          assignedTo: users.find(u => u.id === lead.assignedTo)?.name || lead.assignedTo,
          attachment: lead.attachment,
          // Include history if available
          history: lead.history?.map(entry => ({
            date: formatDate(entry.date),
            type: entry.type,
            changes: entry.changes,
            user: entry.user
          }))
        };
      });

      // Convert to JSON string with nice formatting
      const jsonString = JSON.stringify(exportLeads, null, 2);

      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', `leads_export_${format(new Date(), "yyyy-MM-dd")}.json`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export erfolgreich",
        description: `${filteredLeads.length} Leads wurden als JSON exportiert`,
      });
    } catch (error) {
      console.error("Error exporting to JSON:", error);
      toast({
        title: "Export fehlgeschlagen",
        description: "Die Leads konnten nicht exportiert werden.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 bg-white border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{`Lead Liste von ${currentUser?.name}`}</h1>
              <p className="text-sm text-gray-500">Verwalten Sie Ihre Leads effizient</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportOpen(true)}
              >
                <FileUp className="h-4 w-4 mr-2" />
                CSV Import
              </Button>
              <Button
                variant="default"
                size="sm"
                className="bg-primary hover:bg-primary/90 text-white"
                onClick={handleNewLead}
              >
                <Plus className="h-4 w-4 mr-2" />
                Neuer Lead
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between border-b pb-4">
            <div className="flex">
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none border-b-2 ${activeView === 'board' ? 'border-primary text-primary' : 'border-transparent text-gray-600'} px-4`}
                onClick={() => setActiveView('board')}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Board
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none border-b-2 ${activeView === 'list' ? 'border-primary text-primary' : 'border-transparent text-gray-600'} px-4`}
                onClick={() => setActiveView('list')}
              >
                <List className="h-4 w-4 mr-2" />
                Liste
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none border-b-2 ${activeView === 'table' ? 'border-primary text-primary' : 'border-transparent text-gray-600'} px-4`}
                onClick={() => setActiveView('table')}
              >
                <TableIcon className="h-4 w-4 mr-2" />
                Tabelle
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none border-b-2 ${activeView === 'timeline' ? 'border-primary text-primary' : 'border-transparent text-gray-600'} px-4`}
                onClick={() => setActiveView('timeline')}
              >
                <Activity className="h-4 w-4 mr-2" />
                Zeitlinie
              </Button>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 pl-8 w-[200px] text-sm bg-gray-50 border-gray-200"
                />
              </div>

              {currentUser?.role === 'admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={handleMoveToOldContacts}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  In Altkontakte verschieben
                </Button>
              )}

              <Popover open={filtersVisible} onOpenChange={setFiltersVisible}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Filter className="h-4 w-4 mr-2" />
                    Filter
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <h4 className="font-medium">Filter Optionen</h4>

                    <div className="space-y-2">
                      <h5 className="text-sm font-medium">Status</h5>
                      <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
                        <SelectTrigger className="w-[180px]">
                          <Calendar className="mr-2 h-4 w-4" />
                          <SelectValue placeholder="Zeitraum" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Zeiträume</SelectItem>
                          <SelectItem value="today">Heute</SelectItem>
                          <SelectItem value="week">Diese Woche</SelectItem>
                          <SelectItem value="month">Diesen Monat</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Status auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Status</SelectItem>
                          <SelectItem value="new">Neu</SelectItem>
                          <SelectItem value="old-contact">Altkontakte</SelectItem>
                          <SelectItem value="not-reached">Nicht erreicht</SelectItem>
                          <SelectItem value="follow-up">Wiedervorlage</SelectItem>
                          <SelectItem value="scheduled">Terminiert</SelectItem>
                          <SelectItem value="in-progress">In Prozess</SelectItem>
                          <SelectItem value="won">Gewonnen</SelectItem>
                          <SelectItem value="lost">Verloren</SelectItem>
                          <SelectItem value="wrong-data">Falsche Daten</SelectItem>
                          <SelectItem value="delete-data">Daten löschen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-medium">Priorität</h5>
                      <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Priorität auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Prioritäten</SelectItem>
                          <SelectItem value="low">Niedrig</SelectItem>
                          <SelectItem value="medium">Mittel</SelectItem>
                          <SelectItem value="high">Hoch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(currentUser?.role === "admin" || currentUser?.role === "developer") && (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium">Zugewiesen an</h5>
                        <Select
                          value={selectedUserId === "all" ? "all-leads" : (selectedUserId || currentUser?.id)}
                          onValueChange={(value) => {
                            if (value === "all-leads") {
                              handleUserSelect("all");
                            } else {
                              handleUserSelect(value);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>
                              {selectedUserId === "all" ? "Alle Leads" :
                                users.find(u => u.id === selectedUserId)?.name ||
                                users.find(u => u.id === currentUser?.id)?.name ||
                                "Zugewiesen an"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all-leads">Alle Leads</SelectItem>
                            {users
                              .filter(user => !hideDeactivatedUsers || user.active !== false)
                              .map((user) => (
                              <SelectItem
                                key={user.id}
                                value={user.id}
                                className={!user.active ? "text-muted-foreground opacity-60" : ""}
                              >
                                {user.name || "Unbekannter Benutzer"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="hideOldContacts"
                          checked={hideOldContacts}
                          onCheckedChange={(checked) => setHideOldContacts(checked as boolean)}
                        />
                        <label
                          htmlFor="hideOldContacts"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Altkontakte ausblenden
                        </label>
                      </div>
                      {(currentUser?.role === "admin" || currentUser?.role === "developer") && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hideDeactivatedUsers"
                            checked={hideDeactivatedUsers}
                            onCheckedChange={(checked) => setHideDeactivatedUsers(checked as boolean)}
                          />
                          <label
                            htmlFor="hideDeactivatedUsers"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Deaktivierte Nutzer ausblenden
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setStatusFilter("all");
                          setPriorityFilter("all");
                          setAssignedToFilter("all");
                          setHideOldContacts(false);
                          setHideDeactivatedUsers(true);
                        }}
                      >
                        Zurücksetzen
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setFiltersVisible(false)}
                      >
                        Anwenden
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Lädt...' : 'Aktualisieren'}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Sortieren
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Sortieren nach</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleSort("status")}>
                    Status {sortField === "status" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("name")}>
                    Name {sortField === "name" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("position")}>
                    Beruf {sortField === "position" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("attachment")}>
                    Aufhänger {sortField === "attachment" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("priority")}>
                    Priorität {sortField === "priority" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("lastContactDate")}>
                    Letzter Kontakt {sortField === "lastContactDate" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort("nextContactDate")}>
                    Nächster Kontakt {sortField === "nextContactDate" && (sortDirection === "asc" ? "↑" : "↓")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {(currentUser?.role === "admin" || currentUser?.role === "developer") && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-md">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Exportieren</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={exportToExcel}>
                      <FileText className="h-4 w-4 mr-2" />
                      Excel Export
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToJSON}>
                      <FileText className="h-4 w-4 mr-2" />
                      JSON Export
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Zeile unter dem Trennstrich: Pagination + Folder */}
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Folder Selector */}
            <Select
              value={selectedFolderId || "all"}
              onValueChange={(value) => {
                if (value === "manage") {
                  setIsFolderManagementOpen(true);
                } else {
                  setSelectedFolderId(value === "all" ? null : value);
                }
              }}
            >
              <SelectTrigger className="w-[180px] h-8 text-sm">
                <FolderOpen className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Alle Leads" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Leads</SelectItem>
                {folders.length > 0 && (
                  <>
                    <SelectSeparator />
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        <span className="flex items-center">
                          <div
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: folder.color || "#3B82F6" }}
                          />
                          {folder.name}
                        </span>
                      </SelectItem>
                    ))}
                  </>
                )}
                <SelectSeparator />
                <SelectItem value="manage">
                  <span className="flex items-center text-primary">
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Ordner verwalten
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination - rechts (nur bei "Alle Leads" für Admins) */}
          {!isLoading && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {totalLeadsCount} Leads
              </span>
              {/* "Mehr laden" nur bei Admin + "Alle Leads" */}
              {hasMore && selectedUserId === 'all' && (currentUser?.role === 'admin' || currentUser?.role === 'developer') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-sm"
                  onClick={loadMoreLeads}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Lädt..." : `Mehr laden (+${PAGE_SIZE})`}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {renderContent()}
        </div>
      </div>

      {/* Sheet-basierter Lead Drawer anstelle des vaul Drawers */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleLeadSubmit}
        isNew={isNewLead}
        onLeadUpdate={handleLeadUpdate}
      />

      {/* CSV Import Dialog */}
      <CSVImport
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImport={handleImportLeads}
        userFolders={folders}
      />

      {/* Add the AlertDialog */}
      <AlertDialog open={isMoveToOldContactsDialogOpen} onOpenChange={setIsMoveToOldContactsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leads in Altkontakte verschieben</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie wirklich {leadsToMove.length} Leads in Altkontakte verschieben?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMoveToOldContacts}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verschieben
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add the Status Selection Dialog before the Move to Old Contacts Dialog */}
      <Dialog open={isStatusSelectionDialogOpen} onOpenChange={setIsStatusSelectionDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Status für Altkontakte auswählen</DialogTitle>
            <DialogDescription>
              Wählen Sie die Status aus, deren Leads in Altkontakte verschoben werden sollen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Status auswählen</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "new", label: "Neu" },
                  { value: "old-contact", label: "Altkontakt" },
                  { value: "not-reached", label: "Nicht erreicht" },
                  { value: "follow-up", label: "Wiedervorlage" },
                  { value: "scheduled", label: "Terminiert" },
                  { value: "in-progress", label: "In Prozess" },
                  { value: "won", label: "Gewonnen" },
                  { value: "lost", label: "Verloren" },
                  { value: "wrong-data", label: "Falsche Daten" },
                  { value: "delete-data", label: "Daten löschen" },
                  { value: "marketplace-system", label: "Marktplatz-Altkontakte (Grau)" }
                ].map((status) => (
                  <div
                    key={status.value}
                    className={`flex items-center space-x-2 ${status.value === "marketplace-system" ? "mt-3" : ""}`}
                  >
                    <Checkbox
                      id={status.value}
                      checked={selectedStatuses.includes(status.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedStatuses([...selectedStatuses, status.value]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.value));
                        }
                      }}
                    />
                    <label
                      htmlFor={status.value}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {status.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusSelectionDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleConfirmStatusSelection}>
              Weiter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Management Dialog */}
      <Dialog open={isFolderManagementOpen} onOpenChange={(open) => {
        setIsFolderManagementOpen(open);
        if (!open) {
          setEditingFolder(null);
          setNewFolderName("");
          setNewFolderColor("#3B82F6");
        }
      }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Ordner verwalten</DialogTitle>
            <DialogDescription>
              Erstellen, umbenennen oder löschen Sie Ihre persönlichen Ordner.
            </DialogDescription>
          </DialogHeader>

          {/* Create new folder */}
          <div className="space-y-3 mb-4 p-3 border rounded-lg bg-gray-50">
            <div className="text-sm font-medium">Neuen Ordner erstellen</div>
            <div className="flex gap-2">
              <Input
                placeholder="Ordnername"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) {
                    handleCreateFolder(newFolderName.trim(), newFolderColor);
                    setNewFolderName("");
                    setNewFolderColor("#3B82F6");
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={() => {
                  if (newFolderName.trim()) {
                    handleCreateFolder(newFolderName.trim(), newFolderColor);
                    setNewFolderName("");
                    setNewFolderColor("#3B82F6");
                  }
                }}
                disabled={!newFolderName.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                    newFolderColor === color ? "border-gray-800 scale-110" : "border-transparent hover:scale-105"
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewFolderColor(color)}
                />
              ))}
            </div>
          </div>

          {/* Folder list */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-white"
              >
                {editingFolder?.id === folder.id ? (
                  <div className="flex-1 space-y-2">
                    <Input
                      value={editingFolder.name}
                      onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && editingFolder.name.trim()) {
                          handleRenameFolder(editingFolder.id, editingFolder.name.trim());
                          if (editingFolder.color !== folder.color) {
                            handleUpdateFolderColor(editingFolder.id, editingFolder.color || "#3B82F6");
                          }
                          setEditingFolder(null);
                        }
                        if (e.key === "Escape") setEditingFolder(null);
                      }}
                      autoFocus
                      className="mb-2"
                    />
                    <div className="flex gap-1.5 flex-wrap">
                      {FOLDER_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-5 h-5 rounded-full border-2 transition-all ${
                            (editingFolder.color || "#3B82F6") === color ? "border-gray-800 scale-110" : "border-transparent hover:scale-105"
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setEditingFolder({ ...editingFolder, color })}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <span className="flex items-center">
                    <div
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: folder.color || "#3B82F6" }}
                    />
                    <span className="font-medium">{folder.name}</span>
                  </span>
                )}

                <div className="flex gap-1 ml-2">
                  {editingFolder?.id === folder.id ? (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          if (editingFolder.name.trim()) {
                            handleRenameFolder(editingFolder.id, editingFolder.name.trim());
                            if (editingFolder.color !== folder.color) {
                              handleUpdateFolderColor(editingFolder.id, editingFolder.color || "#3B82F6");
                            }
                            setEditingFolder(null);
                          }
                        }}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditingFolder(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingFolder({ ...folder })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={async () => {
                          const success = await handleDeleteFolder(folder.id);
                          if (success && selectedFolderId === folder.id) {
                            setSelectedFolderId(null);
                          }
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {folders.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                Keine Ordner vorhanden. Erstellen Sie Ihren ersten Ordner oben.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFolderManagementOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
